// Apolo IDE — Tests tab (Sprint v11 E9).
//
// Listens for test run lifecycle events:
//   apolo:testRunStart  { runId, command, cwd }
//   apolo:testRunOutput { runId, chunk }
//   apolo:testRunDone   { runId, exitCode, summary }
//
// Auto-trigger: subscribes to apolo:multiEditsApplied and dispatches
//   apolo:requestTestRun { command, cwd } when auto-test toggle is on.
//
// Actual spawning happens in Electron main via window.apolo.test.run; this
// component owns the renderer-side state, history, and the fix-up retry
// counter (max 3 attempts before giving up — sends failurePrompt to the
// composer for human review).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Loader2, Play } from 'lucide-react';
import { parseTestOutput, failurePrompt } from '../../../lib/testOutputParser.js';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { hasElectronBridge } from '../../../lib/electronBridge.js';
import { createSessionRule, ruleTextFromTestFailure } from '../../../lib/sessionRulesClient.js';
import { noteTestOutcome } from '../../../lib/runOutcomeBridge.js';

const STORAGE_KEY = 'apolo:tests:auto';
const MAX_RETRIES = 3;

function readAutoEnabled() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeAutoEnabled(v) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, v ? 'true' : 'false');
  } catch {
    // ignore
  }
}

export default function TestsTab() {
  const [runs, setRuns] = useState([]); // [{id, command, status, output, summary, startedAt, finishedAt}]
  const [activeId, setActiveId] = useState(null);
  const [autoEnabled, setAutoEnabled] = useState(() => readAutoEnabled());
  const [retryCount, setRetryCount] = useState(0);
  const [filterFailures, setFilterFailures] = useState(false);
  const pendingFailRef = useRef(null);
  const passAfterRetryRef = useRef(false);

  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  // Sprint v11 self-improvement loop — pull active session root for spawn cwd.
  const activeRoot = useAgentStore((s) => {
    const id = s.code.activeSessionId;
    const sess = id ? s.code.sessions.find((x) => x.id === id) : null;
    return sess ? (sess.worktree_path || sess.directory || null) : null;
  });

  // Bridge Electron IPC events → window events that this component listens to.
  // Done in a separate effect so it runs once per mount regardless of state.
  useEffect(() => {
    if (!hasElectronBridge() || !window.apolo?.test?.onStart) return undefined;
    const unsubStart = window.apolo.test.onStart((payload) => {
      window.dispatchEvent(new CustomEvent('apolo:testRunStart', { detail: payload }));
    });
    const unsubOutput = window.apolo.test.onOutput((payload) => {
      window.dispatchEvent(new CustomEvent('apolo:testRunOutput', { detail: payload }));
    });
    const unsubDone = window.apolo.test.onDone((payload) => {
      window.dispatchEvent(new CustomEvent('apolo:testRunDone', { detail: payload }));
    });
    return () => {
      try { unsubStart?.(); unsubOutput?.(); unsubDone?.(); } catch { /* ignore */ }
    };
  }, []);

  // Listen for requestTestRun → spawn via Electron IPC with active session root.
  useEffect(() => {
    async function onRequest(e) {
      if (!hasElectronBridge() || !window.apolo?.test?.run) {
        window.dispatchEvent(new CustomEvent('apolo:testRunDone', {
          detail: { runId: 'no-bridge', exitCode: -1, error: 'Electron test IPC ausente' },
        }));
        return;
      }
      if (!activeRoot) {
        window.dispatchEvent(new CustomEvent('apolo:testRunDone', {
          detail: { runId: 'no-root', exitCode: -1, error: 'Sessão sem directory configurada' },
        }));
        return;
      }
      const result = await window.apolo.test.run({ root: activeRoot, command: e?.detail?.command });
      if (!result?.ok) {
        window.dispatchEvent(new CustomEvent('apolo:testRunDone', {
          detail: { runId: 'failed-start', exitCode: -1, error: result?.error || result?.detail || 'spawn failed' },
        }));
      }
    }
    window.addEventListener('apolo:requestTestRun', onRequest);
    return () => window.removeEventListener('apolo:requestTestRun', onRequest);
  }, [activeRoot]);

  useEffect(() => {
    function onStart(e) {
      const { runId, command, cwd } = e.detail || {};
      if (!runId) return;
      setRuns((prev) => [
        { id: runId, command, cwd, status: 'running', output: '', summary: null, startedAt: Date.now() },
        ...prev.slice(0, 49),
      ]);
      setActiveId(runId);
    }
    function onOutput(e) {
      const { runId, chunk } = e.detail || {};
      if (!runId || typeof chunk !== 'string') return;
      setRuns((prev) => prev.map((r) => (r.id === runId ? { ...r, output: r.output + chunk } : r)));
    }
    function onDone(e) {
      const { runId, exitCode } = e.detail || {};
      if (!runId) return;
      setRuns((prev) => prev.map((r) => {
        if (r.id !== runId) return r;
        const summary = parseTestOutput(r.output);
        const ok = summary.ok && (exitCode == null || exitCode === 0);
        return { ...r, status: ok ? 'pass' : 'fail', summary, exitCode, finishedAt: Date.now() };
      }));
    }
    window.addEventListener('apolo:testRunStart', onStart);
    window.addEventListener('apolo:testRunOutput', onOutput);
    window.addEventListener('apolo:testRunDone', onDone);
    return () => {
      window.removeEventListener('apolo:testRunStart', onStart);
      window.removeEventListener('apolo:testRunOutput', onOutput);
      window.removeEventListener('apolo:testRunDone', onDone);
    };
  }, []);

  // Auto-trigger after multi-edits applied.
  useEffect(() => {
    function onEditsApplied() {
      if (!autoEnabled) return;
      if (retryCount >= MAX_RETRIES) return;
      window.dispatchEvent(new CustomEvent('apolo:requestTestRun', { detail: { source: 'auto' } }));
      setRetryCount((c) => c + 1);
    }
    window.addEventListener('apolo:multiEditsApplied', onEditsApplied);
    return () => window.removeEventListener('apolo:multiEditsApplied', onEditsApplied);
  }, [autoEnabled, retryCount]);

  // Once a run passes, reset the retry counter; on pass after retries, create E2 session rule.
  useEffect(() => {
    const last = runs[0];
    if (!last) return;
    if (last.status === 'pass') {
      noteTestOutcome(activeSessionId, { green_pass: 1, retries: retryCount });
      if (passAfterRetryRef.current && pendingFailRef.current && activeSessionId) {
        const rule = ruleTextFromTestFailure(pendingFailRef.current);
        if (rule) {
          void createSessionRule(activeSessionId, { rule, source: 'e9_fix' }).then((res) => {
            if (res.ok) {
              window.dispatchEvent(new CustomEvent('apolo:sessionRulesUpdated'));
              window.dispatchEvent(new CustomEvent('apolo:toast', {
                detail: { kind: 'success', message: 'Regra aprendida nesta sessão (tab Session).' },
              }));
            }
          });
        }
      }
      pendingFailRef.current = null;
      passAfterRetryRef.current = false;
      setRetryCount(0);
    }
    if (last.status === 'fail' && last.summary && autoEnabled) {
      pendingFailRef.current = last.summary;
      passAfterRetryRef.current = retryCount > 0;
      noteTestOutcome(activeSessionId, { green_pass: 0, retries: retryCount });
      const prompt = failurePrompt(last.summary);
      if (prompt) {
        window.dispatchEvent(new CustomEvent('apolo:insertComposer', { detail: { text: '\n' + prompt + '\n' } }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs.length > 0 ? runs[0].status : null, activeSessionId]);

  const handleToggleAuto = useCallback(() => {
    setAutoEnabled((v) => {
      const next = !v;
      writeAutoEnabled(next);
      return next;
    });
  }, []);

  const handleManualRun = useCallback(() => {
    window.dispatchEvent(new CustomEvent('apolo:requestTestRun', { detail: { source: 'manual' } }));
  }, []);

  const visibleRuns = filterFailures ? runs.filter((r) => r.status === 'fail') : runs;
  const active = runs.find((r) => r.id === activeId) || runs[0];

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={handleManualRun}
            className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover"
          >
            <Play size={11} aria-hidden="true" />
            Rodar
          </button>
          <label className="flex items-center gap-1 text-apolo-textTertiary">
            <input
              type="checkbox"
              checked={autoEnabled}
              onChange={handleToggleAuto}
              className="h-3.5 w-3.5 accent-apolo-accent"
            />
            Auto após edits
          </label>
          <label className="flex items-center gap-1 text-apolo-textTertiary">
            <input
              type="checkbox"
              checked={filterFailures}
              onChange={() => setFilterFailures((v) => !v)}
              className="h-3.5 w-3.5 accent-apolo-error"
            />
            Só falhas
          </label>
        </div>
        {retryCount > 0 && (
          <span className="text-[11px] text-apolo-warning">
            retry {retryCount}/{MAX_RETRIES}
          </span>
        )}
      </header>

      <ul className="max-h-32 shrink-0 overflow-y-auto border-b border-apolo-borderSubtle">
        {visibleRuns.length === 0 && (
          <li className="px-3 py-4 text-center text-xs text-apolo-textTertiary">
            Nenhum run ainda. Clique &quot;Rodar&quot; ou aplique edits com auto-test ligado.
          </li>
        )}
        {visibleRuns.map((r) => {
          const Icon = r.status === 'running' ? Loader2 : r.status === 'pass' ? CheckCircle2 : AlertTriangle;
          const color = r.status === 'pass' ? 'text-apolo-success' : r.status === 'fail' ? 'text-apolo-error' : 'text-apolo-textTertiary';
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setActiveId(r.id)}
                aria-current={r.id === activeId}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition hover:bg-apolo-surface2 ${r.id === activeId ? 'bg-apolo-surface2' : ''}`}
              >
                <Icon size={11} className={`${color} ${r.status === 'running' ? 'animate-spin' : ''}`} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-apolo-textSecondary">
                  {r.command || 'test run'}
                </span>
                {r.summary && (
                  <span className="text-[10px] text-apolo-textTertiary">
                    {r.summary.summary.passed}/{r.summary.summary.total}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex-1 overflow-y-auto bg-apolo-bg p-3 text-xs">
        {!active && (
          <p className="text-apolo-textTertiary">Selecione um run pra ver o output.</p>
        )}
        {active && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-apolo-textSecondary">
              <Activity size={11} aria-hidden="true" />
              <code className="truncate">{active.command || 'test'}</code>
              {active.exitCode != null && (
                <span className="text-[10px] text-apolo-textTertiary">exit {active.exitCode}</span>
              )}
            </div>
            {active.summary?.firstFailure && (
              <div className="rounded border border-apolo-error/30 bg-apolo-errorBg p-2 text-apolo-error">
                <div className="font-medium">{active.summary.firstFailure.name}</div>
                {active.summary.firstFailure.error && (
                  <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap text-[11px]">
                    {active.summary.firstFailure.error}
                  </pre>
                )}
              </div>
            )}
            <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded bg-apolo-surface2 p-2 font-mono text-[11px] text-apolo-textSecondary">
              {active.output || <span className="text-apolo-textTertiary">Sem output ainda…</span>}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
